import React, { useEffect, useRef, useState } from 'react';
import type { Editor } from '@meitu/saas-editor-core/es/editor';
import { type EventEmitter } from '@meitu/saas-editor-core/es/framework/services/events';
import { isHotKeyPress } from '@meitu/saas-editor-core/es/utils/event-kit';
import { CanvasType, IExtraInfo, ILayerProps, PosterType } from '@meitu/saas-editor-core/es/version-types';
import { type FontItem } from '@meitu/xdesign-widgets';
import { StageLoading } from '../stage-loading';
import { StageListScroll } from './list-scroll';
import { StageItem } from './stage';

type StageListProps = {
    recordId: string;
    setIsAiCreate: (isAiCreate: boolean) => void;
    setAiRegen: (data: { isAiRegen: boolean; regenIndex?: number }) => void;
    editor: Editor;
    events: EventEmitter;
    posterType: PosterType;
    canvasType: CanvasType;
    checkIsCover: (type: any) => boolean;
    closeAiPPTPrompt: () => void;
    stageExtraInfo?: { [key: string]: any };
    isAiPPT: boolean;
    isAiRegen: boolean;
    initPPT: (stageIndex?: number) => void | Promise<void>;
    regenIndex: number;
    transferIdParams: any;
    isFromHuaweiCustom: () => boolean;
    getCurrentScene: () => string;
    isFullscreen: boolean;
    showCreateTip: (tip: string) => void;
    hideCreateTip: () => void;
    transferTemplateToClient: (templateDetail: any, fontList: FontItem[]) => Promise<any>;
    packageTemplateConf: (
        templateConf: any,
        extraInfo: IExtraInfo,
        init: boolean,
    ) => {
        stages: ILayerProps[][];
        extraInfos: IExtraInfo[];
    };
    fetchPPTApplyItem: (params: any) => Promise<any>;
    fetchPPTCheckItemProcess: (params: any) => Promise<any>;
    fetchPPTItemDetail: (params: any) => Promise<any>;
    templateSubject: string;
    fetchAIPPTComponentList: (data: any) => Promise<any>;
    getStagePoints: (layers: ILayerProps[], page_type: string) => number;
    replaceComponentKit: (
        componentUrl: string,
        templateUrl: string,
        layers: ILayerProps[],
        fonts: any[],
        page_type: string,
        themeType: string,
    ) => Promise<any[]>;
};
export const StageList = React.memo(
    ({
        recordId,
        setIsAiCreate,
        setAiRegen,
        editor,
        events,
        posterType,
        canvasType,
        checkIsCover,
        closeAiPPTPrompt,
        stageExtraInfo,
        isAiRegen,
        initPPT,
        regenIndex,
        transferIdParams,
        isFromHuaweiCustom,
        getCurrentScene,
        isFullscreen,
        showCreateTip,
        hideCreateTip,
        transferTemplateToClient,
        packageTemplateConf,
        fetchPPTApplyItem,
        fetchPPTCheckItemProcess,
        fetchPPTItemDetail,
        templateSubject,
        fetchAIPPTComponentList,
        getStagePoints,
        replaceComponentKit,
    }: StageListProps) => {
        const [renderStageList, setRenderStageList] = useState<number[]>([]);
        const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
        const [activeIndex, setActiveIndex] = useState<number>(editor.services.frame.getActiveIndex());

        useEffect(() => {
            setRenderStageList([...editor.services.frame.getRenderFrameIds()]);
            const handler = editor.services.frame.onRerenderAll((renderStageList) => {
                setRenderStageList([...renderStageList]);
            });
            return () => editor.off(handler);
        }, []);

        // 监听 stage 选中状态变化事件, 更新 selectedIndexes
        useEffect(() => {
            const handler = editor.services.frame.onFrameSelectionChange((indexes) => {
                setSelectedIndexes(indexes);
            });
            return () => editor.services.events.off(handler);
        }, []);

        // 监听 z 键按下事件, 下面的 onStageChange 里面需要排除掉快捷键撤销的情况
        const isZPressed = useRef(false);
        useEffect(() => {
            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'z') {
                    isZPressed.current = true;
                }
            };
            const handleKeyUp = (e: KeyboardEvent) => {
                isZPressed.current = false;
            };
            document.addEventListener('keydown', handleKeyDown);
            document.addEventListener('keyup', handleKeyUp);
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
                document.removeEventListener('keyup', handleKeyUp);
            };
        }, []);

        useEffect(() => {
            const handler = editor.services.frame.onActiveIndexChange((stageIndex) => {
                setActiveIndex(stageIndex);

                // @TODO 这个设计太绕了, 后续需要寻找更直接的实现方式
                // 正常操作, stage change 的时候需要同时改成选中状态, 但是多选的情况需要排出掉
                // 还需要排除掉用 cmd + z 来撤销的情况
                const hotKeyPressed = isHotKeyPress() && !isZPressed.current;
                if (!hotKeyPressed) {
                    setSelectedIndexes([stageIndex]);
                }
            });
            return () => editor.off(handler);
        }, []);

        // 监听 stage 重新渲染事件, 更新 activeIndex 和 selectedIndexes
        useEffect(() => {
            const handler = editor.services.frame.onRerenderAll(() => {
                const activeIndex = editor.services.frame.getActiveIndex();
                setActiveIndex(activeIndex);
                setSelectedIndexes([activeIndex]);
            });
            return () => editor.off(handler);
        }, []);

        return (
            <>
                <StageListScroll editor={editor} events={events} />
                {renderStageList.map((originIndex, index) => {
                    return (
                        <StageItem
                            key={originIndex}
                            originIndex={originIndex}
                            stageIndex={index}
                            selectedIndexes={selectedIndexes}
                            activeIndex={activeIndex}
                            editor={editor}
                            events={events}
                            isAiRegen={isAiRegen}
                            initPPT={initPPT}
                            regenIndex={regenIndex}
                            transferIdParams={transferIdParams}
                            isFromHuaweiCustom={isFromHuaweiCustom}
                            getCurrentScene={getCurrentScene}
                            isFullscreen={isFullscreen}
                            showCreateTip={showCreateTip}
                            hideCreateTip={hideCreateTip}
                            transferTemplateToClient={transferTemplateToClient}
                            packageTemplateConf={packageTemplateConf}
                            fetchPPTApplyItem={fetchPPTApplyItem}
                            fetchPPTCheckItemProcess={fetchPPTCheckItemProcess}
                            fetchPPTItemDetail={fetchPPTItemDetail}
                            templateSubject={templateSubject}
                            posterType={posterType}
                            canvasType={canvasType}
                            checkIsCover={checkIsCover}
                            closeAiPPTPrompt={closeAiPPTPrompt}
                            stageExtraInfo={stageExtraInfo}
                            recordId={recordId}
                            setIsAiCreate={setIsAiCreate}
                            setAiRegen={setAiRegen}
                            fetchAIPPTComponentList={fetchAIPPTComponentList}
                            getStagePoints={getStagePoints}
                            replaceComponentKit={replaceComponentKit}
                        />
                    );
                })}
                {/* ai ppt 逐页生成 loading */}
                <StageLoading events={events} theme={editor.getTheme()} />
            </>
        );
    },
);
